/**
 * Maps a thrown error (TanStack Router / CatchBoundary `error`) to the system
 * status the full-page `SystemState` should render.
 *
 * Lives in its own module (mirrors `notFoundActions.js`) so the status→state
 * contract is unit-testable without a DOM — the node-only Vitest setup can't
 * render React — and so the component file keeps exporting only components
 * (react-refresh).
 *
 * Rules (per AUTOORBIT-419):
 * - 403 → 403
 * - 404 → 404
 * - any 5xx → 500
 * - no `error.response` at all (network / timeout / thrown non-HTTP error) → 500
 * - anything else (e.g. a 400 that slipped through) → 500 generic
 *
 * @param {unknown} error
 * @returns {403 | 404 | 500}
 */
function resolveSystemStatus(error) {
  const status = error?.response?.status;

  if (status === 403 || status === '403') {
    return 403;
  }

  if (status === 404 || status === '404') {
    return 404;
  }

  // Network / timeout / non-HTTP throw: no response object to read.
  if (!error?.response) {
    return 500;
  }

  const numeric = Number(status);
  if (Number.isFinite(numeric) && numeric >= 500) {
    return 500;
  }

  return 500;
}

/**
 * True when the error carries no HTTP response (network failure / timeout),
 * so the 500 state can swap in connection-flavored copy.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
function isConnectionError(error) {
  return Boolean(error) && !error?.response;
}

export { isConnectionError, resolveSystemStatus };
export default resolveSystemStatus;
